import { OrganizationType } from "../types/organization.type";
import type { RoleOrgType } from "./role-templates";
import { isRoleOrgType } from "./role-templates";
import type { PermissionAction } from "./types";

export type CatalogEntity =
  | "organization"
  | "user"
  | "role"
  | "document"
  | "pickup_order"
  | "pre_order"
  | "collection_site"
  | "container"
  | "material_type"
  | "delivery"
  | "purchase_order"
  | "offer"
  | "appetite"
  | "truck"
  | "assignment"
  | "driver"
  | "dashboard"
  | "esg_report"
  | "forms_submission"
  | "invoice"
  | "notification"
  | "training_program"
  | "training_record"
  | "report"
  | "upload"
  | "approval";

export const ORG_TYPE_ENTITIES: Record<RoleOrgType, readonly CatalogEntity[]> = {
  [OrganizationType.WASTE_OUTLET]: [
    "pickup_order",
    "pre_order",
    "collection_site",
    "container",
    "material_type",
    "dashboard",
    "esg_report",
    "forms_submission",
    "invoice",
    "notification",
    "report",
    "upload",
    "approval",
    "training_program",
    "training_record",
    "organization",
    "user",
    "role",
    "document",
  ],
  [OrganizationType.RECYCLER]: [
    "delivery",
    "purchase_order",
    "offer",
    "material_type",
    "appetite",
    "truck",
    "dashboard",
    "esg_report",
    "forms_submission",
    "invoice",
    "notification",
    "report",
    "upload",
    "approval",
    "training_program",
    "training_record",
    "organization",
    "user",
    "role",
    "document",
  ],
  [OrganizationType.TRUCK]: [
    "truck",
    "driver",
    "assignment",
    "pickup_order",
    "dashboard",
    "notification",
    "upload",
    "approval",
    "training_program",
    "training_record",
    "organization",
    "user",
    "role",
    "document",
  ],
};

export interface EntityGroup {
  label: string;
  entities: readonly CatalogEntity[];
}

export const ENTITY_GROUPS: Record<RoleOrgType, EntityGroup[]> = {
  [OrganizationType.WASTE_OUTLET]: [
    { label: "Pickup & pre-order", entities: ["pickup_order", "pre_order"] },
    { label: "Sites & containers", entities: ["collection_site", "container", "material_type"] },
    {
      label: "Reports & dashboard",
      entities: ["dashboard", "esg_report", "report", "invoice", "forms_submission"],
    },
    {
      label: "Training & notifications",
      entities: ["training_program", "training_record", "notification", "upload"],
    },
    { label: "Administration", entities: ["organization", "user", "role", "document", "approval"] },
  ],
  [OrganizationType.RECYCLER]: [
    {
      label: "Operations",
      entities: ["delivery", "purchase_order", "offer", "material_type", "appetite", "truck"],
    },
    {
      label: "Reports & dashboard",
      entities: ["dashboard", "esg_report", "report", "invoice", "forms_submission"],
    },
    {
      label: "Training & notifications",
      entities: ["training_program", "training_record", "notification", "upload"],
    },
    { label: "Administration", entities: ["organization", "user", "role", "document", "approval"] },
  ],
  [OrganizationType.TRUCK]: [
    { label: "Fleet & assignments", entities: ["truck", "driver", "assignment", "pickup_order"] },
    {
      label: "Training & notifications",
      entities: ["training_program", "training_record", "notification", "upload", "dashboard"],
    },
    { label: "Administration", entities: ["organization", "user", "role", "document", "approval"] },
  ],
};

const DEFAULT_ACTIONS: PermissionAction[] = ["list", "read", "create", "write", "delete"];

const ENTITY_ACTION_OVERRIDES: Partial<Record<CatalogEntity, PermissionAction[]>> = {
  user: ["list", "write", "delete"],
  role: ["list", "assign"],
  training_program: ["list", "read", "manage"],
  assignment: ["list", "read", "accept", "reject"],
  pickup_order: [
    "list",
    "read",
    "create",
    "write",
    "delete",
    "assign",
    "cancel",
    "complete",
    "reject",
    "rollback_after_pickup",
  ],
  dashboard: ["read"],
  esg_report: ["read"],
  organization: ["read", "write"],
  approval: ["list", "read", "submit", "write"],
};

export function actionsForEntity(entity: CatalogEntity): PermissionAction[] {
  return ENTITY_ACTION_OVERRIDES[entity] ?? DEFAULT_ACTIONS;
}

export function buildPermissionCandidates(
  orgId: string,
  siteId: string,
  entity: CatalogEntity,
  actions: readonly PermissionAction[],
): string[] {
  return actions.map((action) => `recyclee.${orgId}.${siteId}.${entity}.${action}`);
}

export function buildFullCatalogForOrg(
  orgId: string,
  orgType: RoleOrgType,
  siteId: string,
): string[] {
  const entities = ORG_TYPE_ENTITIES[orgType];
  const out: string[] = [`recyclee.${orgId}.*`];
  for (const entity of entities) {
    out.push(...buildPermissionCandidates(orgId, siteId, entity, actionsForEntity(entity)));
    out.push(`recyclee.${orgId}.${siteId}.${entity}.*`);
  }
  return [...new Set(out)];
}

export function resolveCatalogOrgType(orgType: OrganizationType): RoleOrgType | null {
  return isRoleOrgType(orgType) ? orgType : null;
}

export function entityGroupsForOrg(orgType: RoleOrgType): EntityGroup[] {
  return ENTITY_GROUPS[orgType];
}

const ENTITY_GROUP_DESCRIPTIONS: Record<string, string> = {
  "Pickup & pre-order": "Schedule and manage waste collection requests across branches.",
  "Sites & containers": "Branch locations, on-site containers, and material classifications.",
  "Reports & dashboard": "Analytics, ESG reporting, invoices, and operational dashboards.",
  "Training & notifications": "Staff training records, programs, alerts, and file uploads.",
  Administration: "Organization settings, team members, roles, and documents.",
  Operations: "Deliveries, purchase orders, offers, materials, appetite, and fleet.",
  "Fleet & assignments": "Trucks, drivers, route assignments, and pickup coordination.",
};

export function entityGroupDescription(groupLabel: string): string {
  return ENTITY_GROUP_DESCRIPTIONS[groupLabel] ?? "";
}

const ENTITY_DESCRIPTIONS: Record<CatalogEntity, string> = {
  organization: "View and update organization profile and settings.",
  user: "Invite, edit, and remove team members.",
  role: "Assign and manage access roles.",
  document: "Upload and manage shared documents.",
  pickup_order: "Create and track pickup requests for waste collection.",
  pre_order: "Plan upcoming pickups before they are scheduled.",
  collection_site: "Manage branch sites and their configuration.",
  container: "Track containers at each collection site.",
  material_type: "Define waste material types and categories.",
  delivery: "Record and monitor inbound material deliveries.",
  purchase_order: "Manage purchase orders with suppliers.",
  offer: "Review and respond to material offers.",
  appetite: "Set material demand and purchasing appetite.",
  truck: "Fleet vehicles and their details.",
  assignment: "Driver route assignments and job dispatch.",
  driver: "Driver profiles and compliance records.",
  dashboard: "Access operational dashboards and KPIs.",
  esg_report: "Environmental, social, and governance reports.",
  forms_submission: "Submit and review operational forms.",
  invoice: "View and manage invoices.",
  notification: "In-app alerts and notification preferences.",
  training_program: "Training curricula and program management.",
  training_record: "Individual staff training completion records.",
  report: "Generate and export operational reports.",
  upload: "Upload files and attachments.",
  approval:
    "Workflow inbox for sensitive actions requiring justification or approver sign-off.",
};

export function entityDescription(entity: CatalogEntity): string {
  return ENTITY_DESCRIPTIONS[entity] ?? "";
}

const ENTITY_LABEL_OVERRIDES: Partial<Record<CatalogEntity, string>> = {
  pickup_order: "Pickup order",
  pre_order: "Pre-order",
  collection_site: "Collection site",
  material_type: "Material type",
  purchase_order: "Purchase order",
  esg_report: "ESG report",
  forms_submission: "Forms",
  training_program: "Training program",
  training_record: "Training record",
  approval: "Approvals",
};

export function entityDisplayName(entity: CatalogEntity): string {
  if (ENTITY_LABEL_OVERRIDES[entity]) {
    return ENTITY_LABEL_OVERRIDES[entity]!;
  }
  return entity
    .split("_")
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ");
}

export const ACTION_DISPLAY_LABELS: Record<string, string> = {
  list: "List",
  read: "Read",
  create: "Create",
  write: "Write",
  delete: "Delete",
  manage: "Manage",
  assign: "Assign",
  accept: "Accept",
  reject: "Reject",
  cancel: "Cancel",
  complete: "Complete",
  rollback_after_pickup: "Rollback after pickup",
  send: "Send",
  register: "Register",
  scan: "Scan",
  submit: "Submit",
};

export function actionDisplayName(action: string): string {
  return ACTION_DISPLAY_LABELS[action] ?? action.charAt(0).toUpperCase() + action.slice(1);
}

export function entityPermissionSet(
  orgId: string,
  siteId: string,
  entity: CatalogEntity,
): string[] {
  const actions = actionsForEntity(entity);
  const perms = buildPermissionCandidates(orgId, siteId, entity, actions);
  const wildcard = `recyclee.${orgId}.${siteId}.${entity}.*`;
  return [...perms, wildcard];
}
